import { ucFirst } from '@shared/util/string';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SUPPORTED_APP_LANGUAGES } from '../appConstants';
import { COUNTRY_CODES } from '../intlConstants';

type GetDisplayName = <T extends string | null | undefined = string | null | undefined>(
  code: T,
) => T extends null | undefined ? null : string;

const languageInstances = new Map<string, Intl.DisplayNames>();

const getLanguageInstance = (locale: string) => {
  if (!languageInstances.has(locale)) {
    languageInstances.set(locale, new Intl.DisplayNames([locale], { type: 'language' }));
  }
  return languageInstances.get(locale)!;
};

const regionInstances = new Map<string, Intl.DisplayNames>();
const getRegionInstance = (locale: string) => {
  if (!regionInstances.has(locale)) {
    regionInstances.set(locale, new Intl.DisplayNames([locale], { type: 'region' }));
  }
  return regionInstances.get(locale)!;
};

export const useGetCountryName = () => {
  const { locale } = useIntl();
  return useMemo(
    () =>
      ((code: string) =>
        code ? getRegionInstance(locale).of(code) || code : null) as GetDisplayName,
    [locale],
  );
};

export const useGetLanguageName = () => {
  const { locale } = useIntl();
  return useMemo(
    () =>
      ((code: string) =>
        code ? getLanguageInstance(locale).of(code) || code : null) as GetDisplayName,
    [locale],
  );
};

const validatedLanguages = (languages?: readonly string[]) =>
  languages?.length
    ? (languages.filter((code) =>
        SUPPORTED_APP_LANGUAGES.includes(code),
      ) as unknown as typeof SUPPORTED_APP_LANGUAGES)
    : SUPPORTED_APP_LANGUAGES;

const sortNameMap: Record<string, string> = {
  ar: 'Arabia',
  bg: 'Blgararski',
  el: 'Ellinika',
};
export const useSelfNamedInterfaceLocales = (allowedLanguages: readonly string[]) =>
  useMemo(
    () =>
      validatedLanguages(allowedLanguages)
        .map((code) => ({
          code,
          name: ucFirst(getLanguageInstance(code).of(code) || code),
        }))
        .sort(({ name: a, code: codeA }, { name: b, code: codeB }) =>
          (sortNameMap[codeA] || a).localeCompare(sortNameMap[codeB] || b),
        ),
    [allowedLanguages],
  );

export const useLanguages = (codes: readonly string[]) => {
  const { locale } = useIntl();
  return useMemo(
    () =>
      codes
        .map((code) => ({
          code,
          name: getLanguageInstance(locale).of(code) || code,
        }))
        .sort(({ name: a }, { name: b }) => a.localeCompare(b)),
    [codes, locale],
  );
};

export const useInterfaceLocales = (allowedLanguages?: readonly string[]) =>
  useLanguages(validatedLanguages(allowedLanguages));

export type Language = ReturnType<typeof useInterfaceLocales>[number];

export const useCountries = (preferredCountryCode?: string) => {
  const { locale } = useIntl();

  return useMemo(() => {
    const countryName = getRegionInstance(locale);
    const countries = COUNTRY_CODES.map((code) => ({
      id: code,
      text: countryName.of(code) || code,
    }));

    // Extract the preferred country if it exists in the list
    const preferred = countries.find((country) => country.id === preferredCountryCode);
    const sortedCountries = countries
      .filter((country) => country.id !== preferredCountryCode)
      .sort(({ text: a }, { text: b }) => a.localeCompare(b));

    // Return the preferred country first, followed by the sorted countries
    return preferred ? [preferred, ...sortedCountries] : sortedCountries;
  }, [locale, preferredCountryCode]);
};
